import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, type CalendarInfo, type Connection } from '../api';
import { ErrorBox, Loading } from '../components/bk';

const PROVIDER_LABEL = { google: 'Google Calendar', microsoft: 'Outlook / Microsoft 365' } as const;

export function Connections() {
  const [q, setQ] = useSearchParams();
  const [conns, setConns] = useState<Connection[] | null>(null);
  const [error, setError] = useState(q.get('error') ?? '');
  const notice = q.get('connected');
  const adminOk = q.get('admin_consent') === 'ok';

  const load = () => api.get<Connection[]>('/api/connections').then(setConns).catch((e) => setError(e.message));
  useEffect(() => {
    load();
    if (q.get('error') || q.get('connected') || q.get('admin_consent')) setTimeout(() => setQ({}, { replace: true }), 4000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async (provider: string, msKind?: 'work' | 'personal' | 'external') => {
    try {
      const { url } = await api.post<{ url: string }>(`/api/connections/${provider}/start`, { msKind });
      location.assign(url);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const remove = async (c: Connection) => {
    if (!confirm(`Disconnect ${c.accountEmail}?`)) return;
    await api.del(`/api/connections/${c.id}`).then(load).catch((e) => setError(e.message));
  };

  return (
    <>
      <div className="pagehead">
        <div>
          <h1>Calendars & meetings</h1>
          <p className="muted">Busy time from every ticked calendar is hidden from your booking pages.</p>
        </div>
      </div>
      <ErrorBox error={error} />
      {adminOk && <div className="alert ok">Admin approval recorded. People in that organization can now connect their Outlook calendar to eBookMe.</div>}
      {notice && <div className="alert ok">Connected {PROVIDER_LABEL[notice as keyof typeof PROVIDER_LABEL] ?? notice}.</div>}

      <section className="card">
        <h2>Add a connection</h2>
        <div className="row">
          <button onClick={() => connect('microsoft', 'work')}>+ Outlook · work or school</button>
          <button onClick={() => connect('microsoft', 'external')}>+ Outlook · other organization</button>
          <button onClick={() => connect('microsoft', 'personal')}>+ Outlook.com (personal)</button>
          <button onClick={() => connect('google')}>+ Google Calendar</button>
        </div>
        <p className="hint">Add as many accounts as you use; busy time from all of them is blocked. Use "work or school" for your own company's Microsoft 365. Accounts from other organizations (for example a client's Microsoft 365) are connected for availability only: eBookMe reads event times, not their details, and never writes to them. If an organization requires admin approval, ask its IT to approve "eBookMe". Teams meetings are created in the account a link books into.</p>
      </section>

      {!conns ? (
        <Loading />
      ) : (
        conns.map((c) => (
          <ConnectionCard
            key={c.id}
            conn={c}
            onRemove={() => remove(c)}
            onReconnect={() => connect(c.provider, c.provider === 'microsoft' ? (c.msKind ?? 'work') : undefined)}
            onError={setError}
          />
        ))
      )}
    </>
  );
}

function ConnectionCard({
  conn,
  onRemove,
  onReconnect,
  onError,
}: {
  conn: Connection;
  onRemove: () => void;
  onReconnect: () => void;
  onError: (e: string) => void;
}) {
  const [cals, setCals] = useState<CalendarInfo[] | null>(null);
  const [busy, setBusy] = useState<string[]>(conn.busyCalendarIds);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get<CalendarInfo[]>(`/api/connections/${conn.id}/calendars`).then(setCals).catch((e) => (onError(e.message), setCals([])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conn.id]);

  const toggle = async (id: string, on: boolean) => {
    const next = on ? [...busy, id] : busy.filter((b) => b !== id);
    setBusy(next);
    try {
      await api.put(`/api/connections/${conn.id}`, { busyCalendarIds: next });
      setSaved(true);
      setTimeout(() => setSaved(false), 1200);
    } catch (e) {
      onError((e as Error).message);
    }
  };

  return (
    <section className="card">
      <div className="row between">
        <div>
          <h3 style={{ margin: 0 }}>{PROVIDER_LABEL[conn.provider]}{conn.msKind === 'personal' ? ' (personal)' : ''}</h3>
          {conn.msKind === 'external' && <span className="badge" style={{ marginTop: 4 }}>Availability only</span>}
          <div className="muted small">{conn.accountEmail}</div>
        </div>
        <div className="row">
          {conn.status === 'ok' ? (
            <span className="badge ok">Connected</span>
          ) : (
            <>
              <span className="badge err">Reconnect needed</span>
              <button className="primary" onClick={onReconnect}>Reconnect</button>
            </>
          )}
          <button className="link-btn" onClick={onRemove}>Disconnect</button>
        </div>
      </div>
      <div style={{ marginTop: 12 }}>
        <div className="row between">
          <strong className="small">Check these calendars for conflicts</strong>
          {saved && <span className="small muted">Saved</span>}
        </div>
        {!cals ? (
          <Loading />
        ) : (
          <ul className="list">
            {cals.map((c) => (
              <li key={c.id}>
                <label className="check">
                  <input type="checkbox" checked={busy.includes(c.id)} onChange={(e) => toggle(c.id, e.target.checked)} />
                  {c.name} {c.primary && <span className="badge">Primary</span>}
                </label>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
